import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { isExternalOwner, OwnersEditor, ownersSumTo100, type OwnerRowValue } from './OwnersEditor';

export type { OwnerRowValue } from './OwnersEditor';
export { ownersSumTo100, productSupportsOwners } from './OwnersEditor';

export const OWNER = 'Owner';

// Each owner row is XOR-clean (client_id OR external_name) so the backend's
// exactly-one validator passes; the counter/UI keys are dropped.
export function normaliseOwnersForPayload(owners: OwnerRowValue[]): OwnerRowValue[] {
  return owners.map(owner => {
    const percentage =
      owner.ownership_percentage == null ? null : Number(owner.ownership_percentage);
    const identity = owner.client_id
      ? { client_id: owner.client_id }
      : { external_name: owner.external_name ?? '' };
    return { ...(owner.id ? { id: owner.id } : {}), ...identity, ownership_percentage: percentage };
  });
}

type OwnersPayloadFragment = { owners?: OwnerRowValue[] };

// Resolves the `owners` fragment to merge into a financial-item save payload, shared
// by every product form. Returns null when the captured shares don't total 100% (a
// notification is shown and the caller must abort the save).
//
// A fragment is only emitted for a joint item that actually carries owners. With the
// flag off, a non-joint item, or a joint item with no owners captured, the `owners`
// key is omitted entirely rather than sent as []: EntityManager only derives/reconciles
// when owners is present, so an empty list would delete the backfilled owner row and
// flip is_joint. Omitting it round-trips the item unchanged.
export function resolveOwnersPayload(
  values: FormValuesType,
  product: string,
  flagEnabled: boolean,
): OwnersPayloadFragment | null {
  const isJoint = (values[product] as Record<string, unknown>)?.is_joint === 'true';
  const owners =
    flagEnabled && isJoint ? ((values[OWNER] as OwnerRowValue[] | undefined) ?? []) : [];

  if (owners.length > 0 && !ownersSumTo100(owners)) {
    notifications.show({
      title: 'Ownership shares must total 100%',
      message: 'Adjust each owner’s share so they add up to 100% before saving.',
      color: 'red',
    });
    return null;
  }

  return owners.length > 0 ? { owners: normaliseOwnersForPayload(owners) } : {};
}

type Props = {
  product: string;
  /** The product field holding the item's total value (e.g. 'current_balance' for spending accounts). */
  valueField?: string;
  variant?: 'classic' | 'valuation';
  /** Lets the Valuation section explain the external slice only when there is one. */
  onExternalOwnersChange?: (hasExternalOwners: boolean) => void;
};

// Create-form adapter: bridges the dynamic form + owners URL param to the controlled
// OwnersEditor and keeps the form's `Owner` list and `is_joint` fields in sync.
export function OwnerSharesSection({
  product,
  valueField = 'value',
  variant,
  onExternalOwnersChange,
}: Props) {
  const currency = useCurrency();
  const { form, isEditing } = useDynamicUI();
  const [searchParams] = useSearchParams();
  const { members } = useFamilyMembers();

  const productValues = (form.getValues()[product] ?? {}) as Record<string, unknown>;
  // Auto-enable joint when the owner-selection step passed joint=true (choosing 2+
  // family members), since useJointOwnerDefault only sets the form value in a later
  // effect and this local switch state wouldn't otherwise pick it up.
  const [isJoint, setIsJoint] = useState(
    () => productValues.is_joint === 'true' || searchParams.get('joint') === 'true',
  );

  // Existing owners (edit, or already seeded) win; otherwise seed from the create
  // flow's owners param. Edit routes never carry an owners param, so no isCreateMode
  // guard is needed.
  const [owners, setOwners] = useState<OwnerRowValue[]>(() => {
    const existing = (form.getValues()[OWNER] as OwnerRowValue[] | undefined) ?? [];
    if (existing.length > 0) return existing;
    const raw = searchParams.get('owners');
    if (raw) {
      try {
        return (JSON.parse(raw) as OwnerRowValue[]).map(owner => ({
          ...owner,
          ownership_percentage: null,
        }));
      } catch {
        return [];
      }
    }
    return [];
  });

  // Only an edit in progress writes back. `useForm` hands out a fresh object each render, so an
  // unguarded effect re-runs on every one — including the render after Cancel, where it would put
  // the abandoned owners back over the values `form.reset()` had just restored.
  useEffect(() => {
    if (!isEditing) return;
    form.setFieldValue(OWNER, owners);
  }, [owners, form, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    form.setFieldValue(`${product}.is_joint`, isJoint ? 'true' : 'false');
  }, [isJoint, product, form, isEditing]);

  // Cancelling an edit calls `form.reset()`, which restores the form's own fields but cannot
  // reach this local state, so the abandoned owners would be saved by the next unrelated edit.
  // Outside an edit the form holds the truth; mirror it back. Create mode never leaves editing,
  // so the owners seeded from the URL param are not touched here.
  useEffect(() => {
    if (isEditing) return;
    const values = form.getValues();
    setOwners((values[OWNER] as OwnerRowValue[] | undefined) ?? []);
    setIsJoint((values[product] as Record<string, unknown> | undefined)?.is_joint === 'true');
  }, [isEditing, form, product]);

  // The reworked money column is editable, so it has to follow the total as the adviser types
  // it; the form is uncontrolled, so that means watching the field rather than reading it once
  // at render. The classic list only prints the slice and reads the total at render as it
  // always has, so the watch is subscribed either way — hooks cannot be conditional — but only
  // the valuation variant acts on it.
  const isValuation = variant === 'valuation';
  const totalAtRender = Number(productValues[valueField]) || 0;
  const [watchedTotal, setWatchedTotal] = useState(totalAtRender);
  form.watch(`${product}.${valueField}`, ({ value }) => {
    if (!isValuation) return;
    setWatchedTotal(Number(value) || 0);
  });
  const total = isValuation ? watchedTotal : totalAtRender;

  useEffect(() => {
    onExternalOwnersChange?.(owners.some(isExternalOwner));
  }, [owners, onExternalOwnersChange]);

  return (
    <OwnersEditor
      owners={owners}
      onChange={setOwners}
      members={members}
      total={total}
      formatAmount={amount => formatMoney(amount, currency)}
      showJointToggle
      isJoint={isJoint}
      onJointChange={setIsJoint}
      disabled={!isEditing}
      variant={variant}
    />
  );
}
